'use client';

import { useSession } from '@/providers/session-provider';
import { DateTime } from 'luxon';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type PropsWithChildren,
} from 'react';
import { createStore, useStore } from 'zustand';
import { combine, devtools } from 'zustand/middleware';
import { raise } from '../utils';
import { useDeletePreset } from './hooks/use-delete-preset';
import { useGetPreset, type PresetResponse } from './hooks/use-get-preset';
import { useMutatePreset } from './hooks/use-mutate-preset';
import { runMigrations } from './run-migrations';
import {
  PRESETS,
  PRESETS_IDENTIFIERS,
  type AllPresets,
  type GetCurrentPresetSchema,
  type GetPresetConfig,
} from './schema';

type PresetIdentifiers = keyof AllPresets;

type PresetMapValue<TIdentifier extends PresetIdentifiers> = Map<
  GetCurrentPresetSchema<TIdentifier>['key'],
  GetCurrentPresetSchema<TIdentifier>
>;

type IsSingle<TIdentifier extends PresetIdentifiers> =
  GetPresetConfig<TIdentifier>['single'] extends true ? true : false;

type PresetProps = {
  [TIdentifier in PresetIdentifiers]: IsSingle<TIdentifier> extends true
    ? GetCurrentPresetSchema<TIdentifier> | null
    : PresetMapValue<TIdentifier>;
} & {
  defectivePresets: PresetResponse['presets'];
};

function createEmptyStoreState(): PresetProps {
  return {
    [PRESETS_IDENTIFIERS.FASTDOC_TEMPLATE]: new Map(),
    [PRESETS_IDENTIFIERS.SUB_TENANT]: new Map(),
    [PRESETS_IDENTIFIERS.SIDEBAR_ORDER]: null,
    defectivePresets: [],
  };
}

const createPresetStore = (initialProps?: Partial<PresetProps>) => {
  const DEFAULT_PROPS: PresetProps = createEmptyStoreState();

  return createStore(
    devtools(
      combine<PresetProps, object>(
        {
          ...DEFAULT_PROPS,
          ...initialProps,
        },
        () => ({})
      ),
      {
        name: 'preset-store',
      }
    )
  );
};

type PresetStore = ReturnType<typeof createPresetStore>;

const PresetContext = createContext<PresetStore | null>(null);

function setValue<TData extends GetCurrentPresetSchema<PresetIdentifiers>>(
  store: PresetStore,
  data: TData
) {
  store.setState(
    (prevState) => {
      const identifier = data.context;
      return {
        [identifier]:
          prevState[identifier] instanceof Map
            ? new Map(prevState[identifier]).set(data.key, data)
            : data,
      };
    },
    undefined,
    { type: 'setValue', schema: data.context }
  );
}

function getPresetConfig<TIdentifier extends PresetIdentifiers>(
  identifier: TIdentifier
): GetPresetConfig<TIdentifier> {
  return PRESETS[identifier];
}

function migrate<
  TPreset extends PresetResponse['presets'][number],
  TIdentifer extends TPreset['context'],
>(preset: TPreset): GetCurrentPresetSchema<TIdentifer> | null {
  const presetConfig = getPresetConfig<TIdentifer>(
    preset.context as TIdentifer
  );

  if (!presetConfig.migrations) {
    console.error(
      `No migrations specified for schema: ${presetConfig.identifier}`
    );
    return null;
  }

  try {
    const res = runMigrations(preset.data, presetConfig.migrations);

    return {
      owner: preset.owner,
      name: preset.name,
      key: preset.key,
      context: preset.context,
      data: res,
    } as GetCurrentPresetSchema<TIdentifer>;
  } catch (e) {
    console.error(`Failed to run migration ${e}`);
    return null;
  }
}

export function PresetProvider({ children }: PropsWithChildren) {
  const isBotstrapped = useRef(false);
  const isSyncing = useRef(false);
  const { isAuthenticated } = useSession({ required: true });
  const { data: publicPresets } = useGetPreset('public');
  const { data: privatePresets } = useGetPreset('private');

  // const { mutateAsync: deletePreset } = useDeletePreset();
  const { mutateAsync: mutatePreset } = useMutatePreset();

  const presetStore = useRef<PresetStore | null>(null);

  if (presetStore.current === null) {
    presetStore.current = createPresetStore();
  }

  const syncStore = useCallback(
    (presets: PresetResponse['presets']) => {
      if (!presetStore.current) return;

      console.time(isBotstrapped.current ? 'syncing' : 'bootstrap');

      let newStoreState = createEmptyStoreState();

      for (const preset of presets) {
        const presetConfig = getPresetConfig(preset.context);

        if (presetConfig.currentVersion === preset.data.version) {
          const res = presetConfig.currentSchema.safeParse(preset);

          if (res.error) {
            console.error(
              `could not parse schema: ${preset.context} with version ${preset.data.version}, falling back to default`
            );

            newStoreState.defectivePresets.push(preset);
            // deletePreset({
            //   owner: preset.owner,
            //   key: preset.key,
            //   context: preset.context,
            // });

            continue;
          }

          const identifier = res.data.context;
          newStoreState = {
            ...newStoreState,
            [identifier]:
              newStoreState[identifier] instanceof Map
                ? (new Map(newStoreState[identifier]).set(
                    res.data.key,
                    res.data
                  ) satisfies PresetProps[typeof identifier])
                : res,
          };
        } else {
          if (!isSyncing.current) {
            isSyncing.current = true;
          }

          const res = migrate(preset);

          if (!res) {
            console.error(
              `could not migrate schema: ${preset.context} with version ${preset.data.version}, falling back to default.`
            );

            newStoreState.defectivePresets.push(preset);

            /* deletePreset({
              owner: preset.owner,
              key: preset.key,
              context: preset.context,
            }); */

            continue;
          }

          if (res) {
            mutatePreset(res).catch(() =>
              console.error(
                `Could not update migrated preset, ${res.context} with key :${res.key}`
              )
            );

            const identifier = res.context;
            newStoreState = {
              ...newStoreState,
              [identifier]:
                newStoreState[identifier] instanceof Map
                  ? (new Map(newStoreState[identifier]).set(
                      res.key,
                      res
                    ) satisfies PresetProps[typeof identifier])
                  : res,
            };
          }
        }
      }

      presetStore.current.setState(newStoreState, undefined, {
        type: isBotstrapped.current ? 'sync' : 'boostrap',
      });

      console.timeEnd(isBotstrapped.current ? 'syncing' : 'bootstrap');
      if (!isBotstrapped.current) {
        isBotstrapped.current = true;
      }
      isSyncing.current = false;
    },
    [mutatePreset]
  );

  useEffect(() => {
    if (
      privatePresets &&
      publicPresets &&
      isAuthenticated &&
      !isSyncing.current
    ) {
      syncStore([...privatePresets, ...publicPresets]);
    }
  }, [isAuthenticated, privatePresets, publicPresets, syncStore]);

  return (
    <PresetContext.Provider value={presetStore.current}>
      {children}
    </PresetContext.Provider>
  );
}

export function usePreset<
  TIdentifier extends PresetIdentifiers,
  TKey extends IsSingle<TIdentifier> extends false ? string : never = never,
>(
  // need to initialize with null since we can differentiate between passing
  // undefined and not passing key at all. Otherwise, a map will be returned
  // when key is yet defined, but passed, e.g from query res.
  ...args: [TIdentifier, TKey | undefined] | [TIdentifier]
) {
  const [identifier, key] = args;

  // This determines if it should return map or value/null
  const isKeyPassed = args.length === 2;

  const presetContext = useContext(PresetContext);
  if (!presetContext) {
    throw new Error('usePreset must be used inside of a preset provider');
  }
  const {
    data: {
      user,
      session: { customerId },
    },
  } = useSession();

  const state = useStore(presetContext, (state) => {
    const value = state[identifier];
    let res: unknown;
    if (isKeyPassed && value instanceof Map) {
      if (key && value.has(key)) {
        res = value.get(key) as GetCurrentPresetSchema<TIdentifier>;
      } else {
        res = null;
      }
    } else if (value instanceof Map) {
      res = value;
    } else {
      res = value;
    }

    // Sadly typescript is not possible to infere this type
    return res as IsSingle<TIdentifier> extends true
      ? // Value is a single instance
        GetCurrentPresetSchema<TIdentifier> | null
      : [TKey] extends [never]
        ? // No key provided
          PresetMapValue<TIdentifier>
        : // we try to get the value from the map
          GetCurrentPresetSchema<TIdentifier> | null;
  });

  const { mutateAsync: deletePreset } = useDeletePreset<TIdentifier>({
    onSuccess(_, variables) {
      presetContext.setState((prevState) => {
        const identifier = variables.context;
        const prevValue = prevState[identifier];
        const newValue = prevValue instanceof Map ? new Map(prevValue) : null;

        if (newValue instanceof Map) newValue.delete(variables.key);

        return {
          [identifier]: newValue,
        };
      });
    },
  });

  const { mutateAsync: mutatePreset } = useMutatePreset<TIdentifier>({
    onSuccess: (data) => {
      setValue(presetContext, data);
    },
  });

  function remove(preset: GetCurrentPresetSchema<TIdentifier>) {
    return deletePreset({
      owner: preset.owner,
      key: preset.key,
      context: preset.context,
    });
  }

  function mutate(
    publicity: 'private' | 'public',
    newPresetData: Omit<GetCurrentPresetSchema<TIdentifier>, 'data' | 'owner'> & {
      data: Omit<
        GetCurrentPresetSchema<TIdentifier>['data'],
        'createdBy' | 'createdAt' | 'updatedAt' | 'updatedBy'
      >;
    }
  ) {
    if (user === undefined) {
      raise('cant mutate without being signed in');
    }

    const owner = (
      publicity === 'private'
        ? (`private:${customerId}:${user?.id}` as const)
        : (`public:${customerId}` as const)
    ) satisfies GetCurrentPresetSchema<TIdentifier>['owner'];

    const prevData = (
      state instanceof Map ? state.get(newPresetData.key) : state
    ) as GetCurrentPresetSchema<TIdentifier> | undefined;

    let data: unknown;

    if (prevData) {
      data = {
        ...prevData,
        ...newPresetData,
        data: {
          ...newPresetData.data,
          createdAt: prevData.data.createdAt,
          createdBy: prevData.data.createdBy,
          updatedBy: user.id,
          updatedAt: DateTime.now().toISO(),
        },
      };
    } else {
      data = {
        context:
          newPresetData.context satisfies GetCurrentPresetSchema<TIdentifier>['context'],
        name: newPresetData.name satisfies GetCurrentPresetSchema<TIdentifier>['name'],
        key: newPresetData.key satisfies GetCurrentPresetSchema<TIdentifier>['key'],
        data: {
          presetData: newPresetData.data
            .presetData satisfies GetCurrentPresetSchema<TIdentifier>['data']['presetData'],
          version: newPresetData.data
            .version satisfies GetCurrentPresetSchema<TIdentifier>['data']['version'],
          createdAt:
            DateTime.now().toISO() satisfies GetCurrentPresetSchema<TIdentifier>['data']['createdAt'],
          createdBy: user.id,
          updatedBy: user.id,
          updatedAt:
            DateTime.now().toISO() satisfies GetCurrentPresetSchema<TIdentifier>['data']['updatedBy'],
        },
        owner: owner satisfies GetCurrentPresetSchema<TIdentifier>['owner'],
        // typescript sucks
      } as GetCurrentPresetSchema<TIdentifier>;
    }

    const parsedData = getCurrentSchema(identifier).safeParse(data);

    if (!parsedData.success) {
      return Promise.reject(parsedData.error);
    }

    return mutatePreset(parsedData.data as GetCurrentPresetSchema<TIdentifier>);
  }

  return {
    state,
    remove,
    mutate,
  };
}

type DO_NOT_USE_OR_YOU_WILL_BE_FIRED_INTERNAL_PRESET_STORE = unknown;

export function useInternalPresetStore(): PresetStore &
  DO_NOT_USE_OR_YOU_WILL_BE_FIRED_INTERNAL_PRESET_STORE {
  const presetContext = useContext(PresetContext);
  if (!presetContext) {
    throw new Error('usePreset must be used inside of a preset provider');
  }

  return presetContext;
}

function getCurrentSchema<TIdentifier extends PresetIdentifiers>(
  identifier: TIdentifier
): GetPresetConfig<TIdentifier>['currentSchema'] {
  return getPresetConfig(identifier)
    .currentSchema as GetPresetConfig<TIdentifier>['currentSchema'];
}
